<template>
    <div :style="{ '--header-bg-color': primaryColor, '--header-font-color': fontColor }" class="w-100 page-wrapper">
        <div v-if="reservationData.techReservation" class="d-flex align-items-center flex-column text-center mb-4">
            <img
                v-if="reservationData.companyLogo"
                :src="reservationData.companyLogo"
                class="header-logo mb-2"
                alt=""
            />
            <h1 class="company-name">{{ reservationData.companyName }}</h1>
            <span class="company-address">{{ reservationData.address }}</span>
        </div>
        <div class="cards-wrapper w-100" v-if="surveyComplete">
            <b-card
                v-for="data in reservationData.techReservation"
                :key="data.property_name"
                class="template sidebar-header-card w-100 mb-2"
            >
                <template #header
                    ><font-awesome-icon :icon="['fas', 'hotel']" size="lg" class="mr-2" />{{
                        data.property_name
                    }}</template
                >
                <b-card-body class="d-flex justify-content-around flex-md-row flex-column">
                    <img class="thumbnail-image" v-if="data.thumbnail" :src="data.thumbnail" alt="" />

                    <div class="property__check-in-out">
                        <div class="property__check-in-out__check-in text-center">
                            <div class="property__check-in-out__check-in__label">Check-in</div>
                            <div class="property__check-in-out__check-in__date">
                                {{ formatDate(data.from_date) }}
                            </div>
                            <div class="property__check-in-out__check-in__time">
                                from
                                {{ formatTime(data.from_date, data.check_in) }}
                            </div>
                        </div>
                        <div class="property__check-in-out__check-out text-center">
                            <div class="property__check-in-out__check-out__label">Check-out</div>
                            <div class="property__check-in-out__check-out__date">
                                {{ formatDate(data.to_date) }}
                            </div>
                            <div class="property__check-in-out__check-out__time">
                                by
                                {{ formatTime(data.to_date, data.check_out) }}
                            </div>
                        </div>
                    </div>
                </b-card-body>
            </b-card>
            <b-card v-if="!surveyResults" class="template sidebar-header-card survey-card w-100">
                <template #header
                    ><font-awesome-icon :icon="['fas', 'poll']" size="lg" class="mr-2" />
                    {{ surveyTitle }}
                </template>
                <form-generator :surveyJSON="surveyJSON" :onCompleted="surveyComplete" :primaryColor="primaryColor" />
            </b-card>
        </div>
        <div class="w-100" v-if="surveyResults">
            <b-card class="template sidebar-header-card w-100">
                <template #header
                    ><font-awesome-icon :icon="['fas', 'poll']" size="lg" class="mr-2" />{{
                        isResults ? 'Results' : 'Thanks!'
                    }}</template
                >
                <form-generator
                    v-if="surveyResults && isResults"
                    :isReadOnly="isReadOnly"
                    :results="surveyObj"
                    :surveyJSON="surveyJSON"
                    :onCompleted="surveyComplete"
                />
                <b-card-body v-else> Thanks! Form has been submitted. </b-card-body>
            </b-card>
        </div>
    </div>
</template>

<script>
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import unescape from 'lodash/unescape';
    import escape from 'lodash/escape';
    import api from '../../api';
    import { HOSTED_FORM_SAVE_ENDPOINT } from '../../endpoints';
    import FormInput from '../generic/form-input/form-input.vue';
    import starRating from '../generic/star-rating/star-rating.vue';
    import isEmpty from 'lodash/isEmpty';

    export const prepareSurveyResults = (surveyResults, elements) => {
        const surveyObj = {};
        if (surveyResults) {
            elements.forEach((item) => {
                const surveyAnswer = surveyResults?.find((result) => {
                    return (
                        unescape(result.title) === unescape(item.title) ||
                        unescape(result.title) === unescape(item.name)
                    );
                });

                if (surveyAnswer && surveyAnswer?.value === undefined) {
                    if (item.choices) {
                        surveyAnswer.value = item.choices.find(
                            (choice) => choice.text === unescape(surveyAnswer?.displayValue)
                        )?.value;
                    } else {
                        surveyAnswer.value = unescape(surveyAnswer?.displayValue);
                    }
                }
                let value = unescape(surveyAnswer?.value || surveyAnswer?.displayValue || '');
                if (item.type === 'checkbox') {
                    const choices = item.choices;
                    value = choices
                        .filter(
                            (choice) =>
                                value.split(';;').includes(choice.value || choice) ||
                                value.split(';;').includes(choice.text || choice)
                        )
                        .map((c) => unescape(c.value || c));
                }
                if (item.choices && surveyAnswer?.value === 'other') {
                    surveyObj[item.name + '-Comment'] = unescape(surveyAnswer?.displayValue);
                }

                surveyObj[item.name] = value;
            });
        }
        return surveyObj;
    };

    export const prepareSurveyDataToSend = (survey, surveyJSON) =>
        survey.getPlainData().map(({ title, value, name, displayValue }) => {
            const json = JSON.parse(surveyJSON);
            const type = json.elements.find((item) => item.name === name)?.type;
            let inputValue = displayValue;
            let initialValue = value;
            if (type === 'checkbox') {
                inputValue = json.elements
                    .find((item) => item.name === name)
                    ?.choices.filter((choice) => value.includes(choice.value || choice))
                    .map((item) => item.text || item)
                    .join(';;');
                initialValue = value.join(';;');
            }
            const mergeTag = json.elements.find((item) => item.name === name)?.mergeTag || '';
            return {
                title: escape(title),
                displayValue: escape(inputValue) || 'N/A',
                type,
                value: initialValue,
                mergeTag,
            };
        });

    const FormGenerator = () =>
        import(
            /* webpackChunkName: "lx-form-generator" */
            './form-generator.vue'
        );

    export default {
        components: {
            FormGenerator,
            FormInput,
            starRating,
        },
        data() {
            let surveyResults = null;
            try {
                surveyResults = window.SUBMITTED_DATA;
            } catch (e) {
                this.errorToast({ text: 'Error parsing survey results' });
            }
            if (!window.FORM_JSON) {
                this.errorToast({ text: 'Reservation not found' });
                return {
                    reservationData: {},
                    surveyJSON: '',
                    isSurveyCompleted: false,
                    surveyResults: null,
                    surveyTitle: '',
                    surveyObj: {},
                };
            }

            const surveyTitle = window.FORM_JSON.title;
            window.FORM_JSON.title = ' ';
            const json = JSON.stringify(window.FORM_JSON);
            const surveyObj = prepareSurveyResults(surveyResults, window.FORM_JSON.elements);

            return {
                reservationData: {
                    companyLogo: window.COMPANY_LOGO,
                    companyName: window.COMPANY_NAME,
                    address: window.ADDRESS,
                    techReservation: window.TECH_RESERVATION,
                },
                surveyJSON: json,
                isSurveyCompleted: !!Number(window.IS_COMPLETED),
                surveyResults: surveyResults,
                surveyTitle,
                surveyObj,
                primaryColor: window.PRIMARY_COLOR,
                fontColor: window.FONT_COLOR,
            };
        },
        computed: {
            reservationFormUUID() {
                return this.$route.params.id;
            },
            isResults() {
                return this.$route.path.indexOf('results') > 0;
            },
            isReadOnly() {
                return this.isResults || !isEmpty(this.surveyResults);
            },
        },
        methods: {
            unescape,
            getQuestionValue(type, value) {
                return type === 'checkbox' ? unescape(value.split(';;').join('\n')) : unescape(value);
            },
            async surveyComplete(survey) {
                try {
                    const data = prepareSurveyDataToSend(survey, this.surveyJSON);
                    this.surveyResults = data;
                    await api.post(HOSTED_FORM_SAVE_ENDPOINT(), {
                        data,
                        uuid: this.reservationFormUUID,
                    });
                    this.isSurveyCompleted = true;
                    this.successToast({ text: 'Thanks! Form has been submitted.' });
                } catch (e) {
                    this.errorToast({ text: 'Could not submit survey' });
                }
            },
            formatDate(date) {
                return format(parseISO(date), 'EEE, dd MMM yyyy');
            },
            formatTime(date, time) {
                return format(parseISO(date + ' ' + time), 'hh:mm a');
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import './styles.scss';

    .page-wrapper {
        --header-bg-color: #0d47a1;
        --header-font-color: #fff;
        max-width: 1140px;
        margin: 0 auto;
        padding: 20px 20px 50px 20px;

        .sidebar-header-card {
            margin-bottom: 10px !important;
            height: 100%;

            :deep(.card-header) {
                background-color: var(--header-bg-color) !important;
                color: var(--header-font-color) !important;
                padding: 10px !important;
                font-weight: bold;
                align-items: center;
                justify-content: left !important;
                min-height: 50px;
                font-size: 16px;
                padding-left: 15px !important;
            }

            :deep(.card-footer) {
                padding: 5px !important;
            }

            :deep(.card-body) {
                padding: 10px;
                padding-top: 10px !important;
            }

            :deep(.card-subtitle) {
                margin-top: 0;
                background-color: #dfdfdf;
                padding: 5px;
            }

            :deep(.card-title) {
                margin-bottom: 0.5rem !important;
            }
        }

        * {
            font-family: 'Lato' !important;
        }

        .header-logo {
            max-height: 100px;
            max-width: 200px;
            border: none;
            object-fit: contain;
        }

        .thumbnail-image {
            max-width: 300px;
            object-fit: contain;
            max-height: 150px;
        }

        .survey-card {
            :deep(.card-body) {
                padding: 0 !important;
            }
        }

        .question-root-checkboxes {
            display: flex !important;
        }

        .property {
            &__check-in-out {
                display: flex;

                &__check-in {
                    border-right: 1px solid rgba(0, 0, 0, 0.1);
                    padding-right: 2rem;
                }

                &__check-out {
                    padding-left: 2rem;
                }

                &__check-in__label,
                &__check-out__label {
                    font-size: 13px;
                    margin-bottom: 0.3rem;
                }

                &__check-in__date,
                &__check-out__date {
                    font-size: 17px;
                    font-weight: bold;
                }

                &__check-in__time,
                &__check-out__time {
                    font-size: 13px;
                }
            }
        }
    }
</style>
